import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { map } from 'rxjs';

const DASHBOARD_URL = '/dashboard';

export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);

  const token = route.queryParams['tkn'];
  if (token) {
    authService.accessToken = token;
    const baseUrl = state.url.split('?')[0];
    if (baseUrl === '/') {
      return router.parseUrl(DASHBOARD_URL);
    }
    return true;
  }

  // Check the authentication status
  return authService.check().pipe(
    map((authenticated) => {
      if (!authenticated) {
        const redirectURL = state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;
        return router.parseUrl(`sign-in?${redirectURL}`);
      }

      authService.restoreUserFromStorage();

      const requiredRoles = route.data.roles as string[];
      const requiredPermissions = route.data['permissions'] as string[];

      const hasRoles = requiredRoles && requiredRoles.length > 0;
      const hasPermissions = requiredPermissions && requiredPermissions.length > 0;

      if (hasRoles || hasPermissions) {
        const userRoles = authService.getRoles();
        const userPermissions = authService.getPermissions();

        let hasRequiredRoles = false;
        let hasRequiredPermissions = false;

        if (hasRoles) {
          hasRequiredRoles = requiredRoles.some(role => userRoles.includes(role));
        }

        if (hasPermissions) {
          hasRequiredPermissions = requiredPermissions.some(permission => userPermissions.includes(permission));
        }

        // OR Logic: Grants access if the user has AT LEAST ONE of the required roles
        // OR AT LEAST ONE of the required permissions.
        // This allows both administrative roles (e.g., AIVA_admin) and specific permissions
        // (e.g., bot_admin, account_read) to grant access to protected routes.
        // Example: /settings can be accessed by AIVA_admin (role) OR users with permissions
        // like bot_admin, account_admin, etc.
        const hasAccess = hasRequiredRoles || hasRequiredPermissions;

        if (!hasAccess) {
          return router.parseUrl(DASHBOARD_URL);
        }
      }

      return true;
    })
  );
};
